import React, { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import GUI, { IGUIProps } from "../GUI";
import { useOverlay } from "./OverlayProvider";
import Timeout from "../../libs/observer/Timeout";

interface ITooltip {
    within:(e:React.PointerEvent<HTMLDivElement>) => boolean
}
type TTooltipOverlayProps = IGUIProps & {
    tooltip:JSX.Element|JSX.Element[]|string,
    onEnter:(e:React.PointerEvent<HTMLDivElement>) => void
    onLeave:(e:React.PointerEvent<HTMLDivElement>) => void
}
const _Tooltip = forwardRef( (props:TTooltipOverlayProps, ref?:ForwardedRef<ITooltip>) => {
    const refSelf = useRef<HTMLDivElement|null>(null)
    useImperativeHandle(ref, () => ({
        within: (e:React.PointerEvent<HTMLDivElement>) => !!refSelf.current?.contains(document.elementFromPoint(e.clientX, e.clientY))
    }), [])
    const onEnter = (e:React.PointerEvent<HTMLDivElement>) => props.onEnter(e)
    const onLeave = (e:React.PointerEvent<HTMLDivElement>) => props.onLeave(e)
    return <div className={GUI.joinClass(props.className)} ref={refSelf} onPointerEnter={onEnter} onPointerLeave={onLeave}>{props.tooltip}</div>
})

export type TTooltipProps = IGUIProps & {
    children:JSX.Element|string|number
    tooltip?:JSX.Element|JSX.Element[]|string|undefined|null
    tooltipClass?:string
}

var g_id = 0;
export default function Tooltip(props:TTooltipProps){
    const [open,setOpen] = useState<boolean>(false)
    const tooltip = useRef<ITooltip|null>(null)
    const followId = useRef<string>(`ftip_${g_id++}`)
    const tooltipId = useRef<string>(`tip_${g_id++}`)
    const timeout = useMemo(() => new Timeout(150), [])

    const {addOverlay,popOverlay} = useOverlay()

    const onTooltip = (node:ITooltip|null) => tooltip.current = node
    const onEnter = (e:React.PointerEvent<HTMLDivElement>) => {
        if (!props.tooltip) return 
        else if (open) return timeout.cancel()
        const Tip = <_Tooltip className={props.tooltipClass} tooltip={props.tooltip} ref={onTooltip} onEnter={onEnterTip} onLeave={onLeave}/>
        addOverlay(tooltipId.current, {children:Tip, backdropClass:"tooltip", anchor:{follow:followId.current}})
        setOpen(true)
    }
    const onLeave = (e:React.PointerEvent<HTMLDivElement>) => timeout.next()
    const onEnterTip = (e:React.PointerEvent<HTMLDivElement>) => timeout.cancel()

    const onHide = () => {
        if (!open) {
            popOverlay(tooltipId.current)
            setOpen(false)
        }
    }

    useEffect(() => {
        timeout.subscribe(onHide)
        return () => { timeout.unsubscribe(onHide)}
    }, [])

    return <div className={GUI.joinClass("tooltipContainer",props.className)} onPointerEnter={onEnter} onPointerLeave={onLeave} follow-id={followId.current}>{props.children}</div>
}